/// <amd-module name="Test/TestPlansHubRefresh/TestCaseLinkedItems/Component/TestCaseLinkedItemsBaseComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import * as Reselect from "Reselect";
import { ITestCaseItem } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorContracts";
import { TestCaseLinkedItemsActions } from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Redux/TestCaseLinkedItemsActions";
import { ILinkedItemsAwareState } from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Redux/TestCaseLinkedItemsContracts";
import * as Resources from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Resources";
import * as Constants from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Constants";
import { ObservableValue } from "VSS/Core/Observable";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { ContentSize } from "VSSUI/Callout";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelCloseButton, PanelContent } from "VSSUI/Panel";
import { ConnectedHeaderCommandBar } from "VSSUI/ReduxHeaderCommandBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { getLinkedItemsModule } from "../Redux/TestCaseLinkedItemsModule";

export interface ITestCaseLinkedItemsProps extends IVssComponentProperties {
    showPanel: ObservableValue<boolean>;
    testCases: ITestCaseItem[];
    selectedIndex: number;
    planId: number;
    selectedTabId: string;
    loadingInProgress: boolean;
    updateSelection(next: boolean): void;
    saveSelectedTab(selectedTabId: string): void;
    fetchLinkedTestSuites(testCaseId: number): void;
    fetchLinkedRequirementsOrBugs(testCaseId: number, selectedTabId: string): void;
    changeSelectedTab(selectedTabId: string): void;
}

/**
 * TestCaseLinkedItemsBaseComponent: Renders the linked items panel
 */
export class TestCaseLinkedItemsBaseComponent extends VssComponent<ITestCaseLinkedItemsProps, IVssComponentContext> {
    private _selectedTabId: ObservableValue<string>;
    private _testCaseName: ObservableValue<string>;
    private _testCaseId: number;
    private _selectedIndex: number;

    constructor(props: ITestCaseLinkedItemsProps, context?: IVssComponentContext) {
        super(props, context);
        this._selectedIndex = props.selectedIndex;
        this._testCaseName = new ObservableValue<string>(this._getSelectedTestCaseName(this._selectedIndex, this.props.testCases));
        this._testCaseId = this._getSelectedTestCaseId(this._selectedIndex, this.props.testCases);
    }

    public render(): JSX.Element {
        let panelContent = <Spinner className="spinner-panel flex-grow" size={SpinnerSize.large} />;

        if (this.props.selectedTabId) {
            this._selectedTabId = new ObservableValue(this.props.selectedTabId);
            panelContent = (
                <div className="testplans-hub-linked-panel-tab">
                    <TabBar onSelectedTabChanged={this._onSelectedTabChanged} selectedTabId={this._selectedTabId} tabSize={TabSize.Tall}>
                        <Tab name={Resources.TestSuitesTabTitle} id={Constants.TestSuitesTabId} />
                        <Tab name={Resources.RequirementsTabTitle} id={Constants.RequirementsTabId} />
                        <Tab name={Resources.BugsTabTitle} id={Constants.BugsTabId} />
                    </TabBar>
                    <Observer selectedTabId={this._selectedTabId}>
                        {(props: { selectedTabId: string }) => {
                            if (props.selectedTabId === Constants.TestSuitesTabId) {
                                return (
                                    <WrappedComponent
                                        dependencies={["ms.vss-test-web.testcase-linked-items"]}
                                        wrappedType="testcase-linked-items-suites-panel"
                                        wrappedProps={{
                                            testCaseId: this._testCaseId
                                        }}
                                    />
                                );
                            } else if (props.selectedTabId === Constants.RequirementsTabId) {
                                return (
                                    <WrappedComponent
                                        dependencies={["ms.vss-test-web.testcase-linked-items"]}
                                        wrappedType="testcase-linked-items-requirements-panel"
                                        wrappedProps={{
                                            testCaseId: this._testCaseId
                                        }}
                                    />
                                );
                            } else {
                                return (
                                    <WrappedComponent
                                        dependencies={["ms.vss-test-web.testcase-linked-items"]}
                                        wrappedType="testcase-linked-items-bugs-panel"
                                        wrappedProps={{
                                            testCaseId: this._testCaseId
                                        }}
                                    />
                                );
                            }
                        }}
                    </Observer>
                </div>
            );
        }
        return (
            <DynamicModuleLoader modules={[getLinkedItemsModule(this._testCaseId, this.props.planId)]}>
                <CustomPanel lightDismiss={false} onDismiss={this._hidePanel} size={ContentSize.Large} calloutClassName="testplans-hub-panel-callout">
                    <CustomHeader>
                        <HeaderTitleArea>
                            <div>
                                <Observer testCaseName={this._testCaseName}>
                                    {(props: { testCaseName: string }) => {
                                        return (
                                            <div className="testcase-name-and-spinner">
                                                <div className="bolt-table-header-cell-text text-ellipsis testcase-name">{props.testCaseName}</div>
                                                {this.props.loadingInProgress && <Spinner className="spinner-top" size={SpinnerSize.medium} />}
                                            </div>
                                        )
                                    }}
                                </Observer>
                                <div className="test-case-linked-header-command-bar">
                                    <ConnectedHeaderCommandBar
                                        showFilter={false}
                                        commandsSelector={CommandSelector}
                                        viewActionsSelector={this._viewActions}
                                    />
                                </div>
                            </div>
                            <div className="linked-panel-header title-m">{Resources.LinkedItems}</div>
                        </HeaderTitleArea>
                        <PanelCloseButton onDismiss={this._hidePanel} />
                    </CustomHeader>
                    <PanelContent>{panelContent}</PanelContent>
                </CustomPanel>
            </DynamicModuleLoader>
        );
    }

    private _getSelectedTestCaseName(selectedIndex: number, testCases: ITestCaseItem[]): string {
        const testCaseName: string = testCases[selectedIndex].name;
        return testCaseName;
    }

    private _getSelectedTestCaseId(selectedIndex: number, testCases: ITestCaseItem[]): number {
        const testCaseId: number = testCases[selectedIndex].id;
        return testCaseId;
    }

    private _onSelectedTabChanged = (newTabId: string) => {
        this._selectedTabId.value = newTabId;
        this.props.changeSelectedTab(newTabId);
        if (newTabId === Constants.RequirementsTabId) {
            this.props.fetchLinkedRequirementsOrBugs(this._testCaseId, newTabId);
        }
        if (newTabId === Constants.BugsTabId) {
            this.props.fetchLinkedRequirementsOrBugs(this._testCaseId, newTabId);
        }
        if (newTabId === Constants.TestSuitesTabId) {
            this.props.fetchLinkedTestSuites(this._testCaseId);
        }
    };

    private _hidePanel = () => {
        this.props.saveSelectedTab(this._selectedTabId.value);
        this.props.showPanel.value = false;
    };

    private _viewActions = (): IHeaderCommandBarItem[] => {
        const commands: IHeaderCommandBarItem[] = [];
        commands.push(
            {
                id: "previous-test-case",
                iconProps: {
                    iconName: "Up"
                },
                subtle: true,
                onActivate: () => {
                    this._switchTestCase(false);
                    this.props.updateSelection(false);
                },
                className: "",
                tooltipProps: {
                    text: Resources.PreviousTestCase
                }
            },
            {
                id: "next-test-case",
                iconProps: {
                    iconName: "Down"
                },
                subtle: true,
                onActivate: () => {
                    this._switchTestCase(true);
                    this.props.updateSelection(true);
                },
                className: "",
                tooltipProps: {
                    text: Resources.NextTestCase
                }
            }
        );
        return commands;
    };

    /*
    show linked items corresponding to the next or the previous test case
    ** @next - true when the next test case is to be selected and false when the previous test case is to be selected
    */
    private _switchTestCase(next: boolean): void {
        if (next) {
            if (this.props.testCases.length === this._selectedIndex + 1) {
                this._selectedIndex = 0;
            }
            else {
                this._selectedIndex = this._selectedIndex + 1;
            }
        }
        else {
            if (this._selectedIndex === 0) {
                this._selectedIndex = this.props.testCases.length - 1;
            }
            else {
                this._selectedIndex = this._selectedIndex - 1;
            }
        }
        this._testCaseId = this.props.testCases[this._selectedIndex].id;
        this._testCaseName.value = this.props.testCases[this._selectedIndex].name;
        this._onSelectedTabChanged(this._selectedTabId.value);
    }
}

function mapStateToProps(state: ILinkedItemsAwareState): Partial<ITestCaseLinkedItemsProps> {
    let selectedTabId, loadingInProgress;
    if (state && state.linkedItemsState) {
        selectedTabId = state.linkedItemsState.selectedTabId;
        loadingInProgress = state.linkedItemsState.loadingInProgress;
    }
    return {
        selectedTabId: selectedTabId,
        loadingInProgress: loadingInProgress
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestCaseLinkedItemsProps> {
    return {
        saveSelectedTab: (selectedTabId: string) => dispatch(TestCaseLinkedItemsActions.saveSelectedTab(selectedTabId)),
        fetchLinkedTestSuites: (testCaseId: number) => dispatch(TestCaseLinkedItemsActions.fetchLinkedTestSuites(testCaseId)),
        fetchLinkedRequirementsOrBugs: (testCaseId: number, selectedTabId: string) =>
            dispatch(TestCaseLinkedItemsActions.fetchLinkedRequirementsOrBugs(testCaseId, selectedTabId)),
        changeSelectedTab: (selectedTab: string) => dispatch(TestCaseLinkedItemsActions.changeSelectedTab(selectedTab)),
        dispatch
    };
}

export const CommandSelector = Reselect.createSelector(
    // Just a placeholder for now; the ConnectedHeaderCommandBar requires this but we don't have any commands
    [() => { }],
    () => {
        return [];
    }
);

export const TestCaseLinkedItemsComponent = connect(mapStateToProps, mapDispatchToProps)(TestCaseLinkedItemsBaseComponent);

Components.add("testcase-linked-items-panel", TestCaseLinkedItemsComponent);
